import {
  HRMonthlyAchievements,
  HRHeading,
  HRActivitiesOverview,
  HRStudentsByLevel,
  HRMostActiveStudents,
  HRStudyPlanHome,
  useHRPortalTranslation,
  useMonthlyAchievements,
  useOEMostActiveStudents,
  useByDateReport,
  useReport,
  useLiveClassReportByPeriod,
  useStudentsByLevel,
  useHomeStudyPlanStatistics,
  SectionPlaceholder,
  flex,
  mb,
  useGroupClassAttendance,
  HRGroupClassAttendanceModal,
  HRActivitiesPieChart,
  OEStatisticsOfActivities,
  HRStudentsHorizontalBarChart,
  saveFile,
} from '@openeducation/hr-rn-components';
import {
  hrManagerAtom,
  useGetActivityIntervals,
  useGetStudyPlanIntervals,
  useGetAllActivitiesOverview,
  useGetStudyPlans,
} from '@openeducation/hr-rn-graphql-providers';
import { studyPlanEnabledAtom } from '@openeducation/hr-rn-jotai-atoms';
import { usePurchaserIds } from '@openeducation/hr-rn-providers';
import { ActivityType, ReportType } from '@openeducation/hr-rn-shared-types';
import { useAtomValue } from 'jotai';
import React, { useCallback } from 'react';
import { View } from 'react-native';

import { useStudentsListProps } from '../../hooks/use-students-list-props';
import { OEPath } from '../../router';

const OEHomeScreen = () => {
  const hrManager = useAtomValue(hrManagerAtom);
  const studyPlanEnable = useAtomValue(studyPlanEnabledAtom);
  const { t } = useHRPortalTranslation();
  const purchaserIds = usePurchaserIds();
  const { data: studyPlansData } = useGetStudyPlans({ purchaserIds });

  const {
    loading: activitiesOverviewLoading,
    data: activitiesOverviewData,
    isError: activitiesOverviewError,
  } = useGetAllActivitiesOverview({ purchaserIds });
  const {
    studentsByLevelLegend,
    studentsByLevelChart,
    studentsByLevelTotal,
    studentsByLevelLoading,
    studentsByLevelConfigurationsError,
  } = useStudentsByLevel();
  const { mostActiveStudentsChartData, ...mostActiveStudentsProps } = useOEMostActiveStudents();
  useGetActivityIntervals({ purchaserIds });

  const { studyPlanIntervalDates } = useGetStudyPlanIntervals({
    purchaserIds,
  });

  const monthlyAchievementsProps = useMonthlyAchievements();
  const studentsListProps = useStudentsListProps(OEPath.Student);
  const historicalReport = useReport(saveFile, ReportType.HISTORICAL);
  const periodReport = useByDateReport(saveFile, ReportType.ACTIVITY_BY_PERIOD);
  const liveClassReportByPeriod = useLiveClassReportByPeriod(saveFile);

  const studyPlanReport = useReport(saveFile, ReportType.STUDY_PLAN);
  const homeStatisticsProps = useHomeStudyPlanStatistics({
    studyPlansData,
    purchaserIds,
    studyPlanIntervalDates,
    onStudentSelect: studentsListProps.onStudentSelect,
    report: studyPlanReport,
  });

  const {
    isGroupClassAttendanceEnabled,
    openGroupClassAttendance,
    closeGroupClassAttendance,
    groupClassAttendanceVisible,
    selectedGroup,
    selectedStudents,
    selectedDate,
    ...groupClassAttendance
  } = useGroupClassAttendance();

  const renderStudentsByLevel = useCallback(() => {
    if (
      studentsByLevelConfigurationsError ||
      (!studentsByLevelLoading && studentsByLevelTotal === 0)
    ) {
      return null;
    }

    return (
      <HRStudentsByLevel isLoading={studentsByLevelLoading} {...studentsByLevelLegend}>
        <HRActivitiesPieChart {...studentsByLevelChart} />
      </HRStudentsByLevel>
    );
  }, [
    studentsByLevelConfigurationsError,
    studentsByLevelLoading,
    studentsByLevelTotal,
    studentsByLevelLegend,
    studentsByLevelChart,
  ]);

  return (
    <>
      <HRHeading
        title={t('home.heading.title', { managerName: hrManager?.name })}
        description={t('home.heading.description')}
        studentsListProps={studentsListProps}
        historicalReport={historicalReport}
        periodReport={periodReport}
        liveClassReportByPeriod={liveClassReportByPeriod}
      />
      {!activitiesOverviewError && (
        <HRActivitiesOverview
          isLoading={activitiesOverviewLoading}
          stats={activitiesOverviewData}
          subtitle={t('home.activitiesOverview.subtitle')}
        />
      )}
      {studyPlanEnable && <HRStudyPlanHome {...homeStatisticsProps} />}
      <View style={[flex('flex-row', 'flex-1')]}>
        <OEStatisticsOfActivities
          isGroupClassAttendanceEnabled={isGroupClassAttendanceEnabled}
          onGroupClassAttendancePress={openGroupClassAttendance}
        />
        {renderStudentsByLevel()}
      </View>

      <HRMonthlyAchievements<ActivityType> {...monthlyAchievementsProps} />
      <HRMostActiveStudents {...mostActiveStudentsProps}>
        <View style={[flex('flex-1'), mb(15)]}>
          {mostActiveStudentsChartData.values.length === 0 ? (
            <SectionPlaceholder text={t('common.chart.noData')} />
          ) : (
            <HRStudentsHorizontalBarChart {...mostActiveStudentsChartData} />
          )}
        </View>
      </HRMostActiveStudents>
      {isGroupClassAttendanceEnabled && (
        <HRGroupClassAttendanceModal
          step={groupClassAttendance.step}
          groups={groupClassAttendance.groups}
          onGroupSelect={groupClassAttendance.onGroupSelect}
          onStudentsSelect={groupClassAttendance.onStudentsSelect}
          students={groupClassAttendance.students}
          onClose={closeGroupClassAttendance}
          visible={groupClassAttendanceVisible}
          onGoBack={groupClassAttendance.onGoBack}
          confirm={groupClassAttendance.confirm}
          onDateChange={groupClassAttendance.onDateChange}
          selectedGroup={selectedGroup}
          selectedStudents={selectedStudents}
          selectedDate={selectedDate}
          onSubmit={groupClassAttendance.onAttendanceSubmit}
          isLoading={groupClassAttendance.addLiveClassProgressLoading}
          success={groupClassAttendance.addLiveClassProgressSuccess}
          error={groupClassAttendance.addLiveClassProgressError}
          stepLabel={groupClassAttendance.stepLabel}
        />
      )}
    </>
  );
};

export default OEHomeScreen;
